import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

/**
 * Query key factory for celebrities
 */
export const celebrityKeys = {
  all: ['celebrities'] as const,
  search: (name: string) => [...celebrityKeys.all, 'search', name] as const,
  detail: (name: string) => [...celebrityKeys.all, 'detail', name] as const,
  details: (name: string) => [...celebrityKeys.all, 'details', name] as const,
};

/**
 * Hook to search for celebrities by name
 */
export function useCelebritySearch(name: string) {
  return useQuery({
    queryKey: celebrityKeys.search(name),
    queryFn: async () => {
      const response = await api.celebrities.searchCelebrities(name);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: name.length >= 2, // Only search when at least 2 characters
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get celebrity details by name
 */
export function useCelebrity(name: string) {
  return useQuery({
    queryKey: celebrityKeys.detail(name),
    queryFn: async () => {
      const response = await api.celebrities.getCelebrity(name);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!name,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get comprehensive celebrity details including stats and audit events
 * @param name Celebrity name to look up
 * @returns Query result with celebrity details, stats, audit events, and global death record
 */
export function useCelebrityDetails(name: string) {
  return useQuery({
    queryKey: celebrityKeys.details(name),
    queryFn: async () => {
      const response = await api.celebrities.getCelebrityDetails(name);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!name,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
